// useMe.ts — fetches current user profile from backend and syncs role into store.
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { bestRoleFromCodes, rolesFromCodes } from '@/auth/rbac';
import { useStore } from '@/store/store';
import { getAccessToken } from '@/auth/devAuth';

export interface MeResponse {
  id: string;
  username: string;
  email: string;
  roles: string[];
  avatar_url?: string | null;
  /** Whether the user has set a picking PIN (drives first-time PIN setup in the picker flow). */
  has_pin?: boolean;
  tenant: { id: string; name: string; is_active: boolean } | null;
  assigned_stores: {
    id: string;
    name: string;
    code: string;
    store_type: string;
    is_active: boolean;
  }[];
}

export function useMe() {
  const { setRoles, setName, setStore, setUserId, setAvatar, store } = useStore();

  const query = useQuery({
    queryKey: ['identity', 'me'],
    queryFn: () => apiFetch<MeResponse>('/api/identity/me/'),
    enabled: !!getAccessToken(),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  // Sync backend roles + name + default store → frontend store (once on first load)
  useEffect(() => {
    if (query.data) {
      if (query.data.id) {
        setUserId(query.data.id);
      }
      if (query.data.roles?.length) {
        // caps = union of all the user's roles; display the most-privileged one
        setRoles(rolesFromCodes(query.data.roles), bestRoleFromCodes(query.data.roles));
      }
      if (query.data.username) {
        setName(query.data.username);
      }
      setAvatar(query.data.avatar_url ?? '');
      // Default / repair the active store for STORE-SCOPED users: land them on one
      // of their stores when the selection is 'ALL' (which they may not use) or
      // out-of-scope (inherited from another account) — otherwise their dashboard
      // 403s on every store-scoped call. All-store roles (ADM/SY/IM) keep whatever
      // they picked — including a deliberate, persisted 'All Stores': the old
      // unconditional `store === 'ALL'` branch silently stomped that choice on
      // every reload, flipping admins to their first store mid-session.
      const assigned = query.data.assigned_stores ?? [];
      if (assigned.length) {
        const allStore = (query.data.roles ?? []).some((r) =>
          ['ADM', 'SY', 'IM'].includes(r.toUpperCase()),
        );
        const inScope = store === 'ALL' || assigned.some((s) => s.id === store);
        if (!allStore && (store === 'ALL' || !inScope)) {
          setStore(assigned[0].id);
        }
      }
    }
  }, [query.data]); // eslint-disable-line react-hooks/exhaustive-deps

  return query;
}
